import { Close } from '@mui/icons-material';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from '../../utility/function/useTranslations';

export interface INationality {
  nationalityId: string;
  natitonalityBookingUrl: string;
  currency: string;
  nationalityName?: string;
}

interface INationalityDropdownProps {
  nationalityData: INationality[];
  selectedNationality: INationality | null;
  onChange: (nationality: INationality | null) => void;
  fontColor?: string;
  booknowbuttoncolor?: string;
  locale: string;
  isMobile?: boolean;
  hasError?: boolean;
  onClearError?: () => void;
  /** Desktop: expand parent iframe like guests when menu opens */
  setEventType?: (eventType: string) => void;
}

export default function NationalityDropdown(props: INationalityDropdownProps) {
  const {
    nationalityData,
    selectedNationality,
    onChange,
    fontColor,
    booknowbuttoncolor,
    locale,
    isMobile = false,
    hasError = false,
    onClearError,
    setEventType,
  } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setEventType?.('nationalityClick');
  };

  const handleClose = () => {
    setOpen(false);
    setEventType?.('');
  };

  const color = fontColor && fontColor !== 'null' ? fontColor : '#fff';

  const chevronRotation = {
    transition: 'transform 0.5s ease',
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  };

  const getNationalityLabel = (nationality: INationality) =>
    nationality?.nationalityName || nationality?.nationalityId;

  const handleChange = (event: any) => {
    const nationalityId = event?.target?.value;
    const nationality = nationalityData?.find((item) => item?.nationalityId === nationalityId) || null;
    onChange(nationality);
  };

  const selectStyle = {
    color: `${color} !important`,
    fontSize: '15px',
    fontFamily: 'Montserrat, sans-serif',
    width: '100%',
    '& .MuiSelect-select': {
      padding: '0px !important',
      paddingTop: isMobile ? '0px !important' : '17px !important',
      paddingRight: isMobile ? '24px !important' : '30px !important',
      color: `${color} !important`,
      fontSize: '15px',
      fontFamily: 'Montserrat, sans-serif',
      textAlign: locale === 'ar' ? 'right' : 'left',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    '& .MuiSelect-icon': { display: 'none' },
  };

  const menuProps = {
    disableScrollLock: !isMobile,
    // Keep menu in-widget (like guests) so iframe overflow/height expansion can show it
    disablePortal: !isMobile,
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    PaperProps: {
      sx: {
        marginTop: '6px',
        borderRadius: '5px',
        boxShadow: '0 3px 6px #00000029',
        zIndex: 999,
        '& .MuiMenuItem-root': {
          fontSize: '14px',
          color: '#000',
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: `${booknowbuttoncolor || '#546477'}1a`,
        },
      },
    },
  };

  const errorMessage = hasError ? (
    <div className="destination-error-msg">
      <span>{t('widget', 'nationalityIsRequired', locale)}</span>
      <span>
        <Close
          onClick={() => {
            onClearError?.();
            setEventType?.('');
          }}
          sx={{ fontSize: '15px', top: '3px', pl: '5px', position: 'relative', cursor: 'pointer', width: '25px !important' }}
        />
      </span>
    </div>
  ) : null;

  const dropdown = (
    <FormControl sx={{ width: '100%' }}>
      <Select
        displayEmpty
        value={selectedNationality?.nationalityId || ''}
        onChange={handleChange}
        variant="outlined"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={isMobile ? () => null : () => (
          <ChevronDown
            className="w-4 h-4 flex-shrink-0"
            style={{ color, position: 'absolute', top: '20px', right: locale === 'ar' ? 'unset' : '12px', left: locale === 'ar' ? '12px' : 'unset', pointerEvents: 'none', ...chevronRotation }}
          />
        )}
        renderValue={(value) => {
          if (!value) {
            return <span style={{ fontSize: '14px', fontWeight: 300, opacity: 1, color }}>{t('widget', 'selectNationality', locale)}</span>;
          }
          const nationality = nationalityData?.find((item) => item?.nationalityId === value);
          return <span>{nationality ? getNationalityLabel(nationality) : ''}</span>;
        }}
        sx={selectStyle}
        MenuProps={menuProps}
      >
        {nationalityData?.map((nationality) => (
          <MenuItem key={nationality?.nationalityId} value={nationality?.nationalityId}>
            {getNationalityLabel(nationality)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (isMobile) {
    return (
      <Box component="div">
        <Typography variant="h6" style={{ color, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
          {t('widget', 'nationality', locale)}
          <ChevronDown className="w-4 h-4 flex-shrink" style={{ color, marginLeft: '8px', ...chevronRotation }} />
        </Typography>
        <Box component="div" className="nationality-control">
          <Box component="div" sx={{ mt: '4px' }}>{dropdown}</Box>
          {errorMessage}
        </Box>
      </Box>
    );
  }

  return (
    <Box component="div" className="nationality">
      <Box
        component="span"
        className="labelText"
        sx={{ color: `${color} !important`, textTransform: 'uppercase' }}
      >
        {t('widget', 'nationality', locale)}
      </Box>
      <Box component="div" className="nationality-control">
        <Box component="div" sx={{ display: 'flex' }}>{dropdown}</Box>
        {errorMessage}
      </Box>
    </Box>
  );
}
